
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Question, Test } from "@/types/test";
import { MonitoringEvent } from "@/types/monitoring";
import { 
  TestResponse, 
  RecruitmentStatus,
  FilterType,
  IntegrityFilter,
  ScoreFilter
} from "@/types/test-response";
import { SortOption } from "@/components/test-details/responses-table/SortAndSearchBar";
import { transformTestResponse } from "@/utils/response-utils";

export const useTestData = (testId?: string) => {
  const { 
    data: testDetails, 
    isLoading: isLoadingTest, 
    error: testError 
  } = useQuery({
    queryKey: ['test', testId],
    queryFn: async () => {
      if (!testId) throw new Error("Test ID is required");
      
      console.log('[useTestData] Starting fetch for testId:', testId);
      
      const { data, error } = await supabase
        .from('tests')
        .select(`
          id,
          title,
          description,
          location,
          created_at,
          status,
          creator_id,
          key_responsibilities,
          years_of_experience,
          key_skills,
          time_limit,
          work_presence,
          work_arrangement,
          job_title,
          job_description,
          language,
          publish_to_job_board,
          salary_range_min,
          salary_range_max,
          webcam_monitoring_enabled,
          screen_monitoring_enabled,
          welcome_message,
          completion_message,
          organization_id
        `)
        .eq('id', testId)
        .maybeSingle();

      if (error) {
        console.error(`Error fetching test details for ${testId}:`, error);
        throw error;
      }
      
      if (!data) {
        console.error(`No test found for id: ${testId}`);
        throw new Error(`Test not found: ${testId}`);
      }

      console.log('[useTestData] Test data fetched:', data);

      // Fetch public employer info - this is critical for company name/logo
      let employer = null;
      if (data.creator_id) {
        console.log('[useTestData] Fetching employer info for creator_id:', data.creator_id);
        
        try {
          const { data: employerData, error: employerError } = await supabase
            .rpc('get_public_employer_info', { employer_id: data.creator_id });
          
          console.log('[useTestData] Employer RPC result:', { employerData, employerError });
          
          if (!employerError && employerData && employerData.length > 0) {
            const emp = employerData[0] as any;
            employer = {
              id: emp.id,
              company_name: emp.company_name || emp.organization || "Company",
              company_logo_url: emp.company_logo_url,
              company_location: emp.company_location,
              company_industry: emp.company_industry,
              organization: emp.organization
            };
            console.log('[useTestData] Successfully set employer data:', employer);
          } else {
            console.warn('[useTestData] No employer data found or error:', employerError);
          }
        } catch (employerFetchError) {
          console.error('[useTestData] Error fetching employer info:', employerFetchError);
        }
      }
      
      const result = { ...data, employer };
      console.log('[useTestData] Final test data with employer:', result);
      return result as Test;
    },
    enabled: !!testId,
    retry: 2,
  });

  const { 
    data: questions, 
    isLoading: isLoadingQuestions, 
    error: questionsError 
  } = useQuery({
    queryKey: ['test-questions', testId],
    queryFn: async () => {
      if (!testId) return [];
      
      const { data, error } = await supabase
        .from('questions')
        .select(`
          id,
          question_type,
          content,
          time_limit,
          order,
          marks,
          skill,
          difficulty,
          markscheme
        `)
        .eq('test_id', testId)
        .order('order', { ascending: true });

      if (error) {
        console.error(`Error fetching questions for ${testId}:`, error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        console.error(`No questions found for test: ${testId}`);
        throw new Error(`No questions found for this test`);
      }
      
      return data as Question[];
    },
    enabled: !!testId && !!testDetails,
    retry: 2,
  });

  // Helper function to separate filter types
  const separateFilters = (filters: FilterType[]) => {
    const recruitmentFilters = filters.filter(f => 
      ['pending', 'shortlisted', 'hired', 'rejected'].includes(f)
    ) as RecruitmentStatus[];
    
    const integrityFilters = filters.filter(f => 
      f.startsWith('integrity_')
    ) as IntegrityFilter[];
    
    const scoreFilters = filters.filter(f => 
      f.startsWith('score_')
    ) as ScoreFilter[];
    
    return { recruitmentFilters, integrityFilters, scoreFilters };
  };

  // Helper function to apply client-side filters
  const applyClientSideFilters = (
    responses: TestResponse[], 
    integrityFilters: IntegrityFilter[], 
    scoreFilters: ScoreFilter[]
  ): TestResponse[] => {
    return responses.filter(response => {
      // Apply integrity filters
      for (const filter of integrityFilters) {
        const threshold = parseInt(filter.split('_')[1]);
        const integrityScore = response.integrity_score ?? 0;
        if (integrityScore < threshold) return false;
      }
      
      // Apply score filters based on stored AI score percentage
      if (scoreFilters.length > 0) {
        const scorePercentage = Math.round(response.total_ai_score || 0);

        for (const filter of scoreFilters) {
          const threshold = parseInt(filter.split('_')[1]);
          if (scorePercentage < threshold) return false;
        }
      }
      
      return true;
    });
  };

  // Server-side paginated query for database sortable columns
  const useResponsesQuery = (
    page = 1,
    pageSize = 15,
    activeFilters: FilterType[] = [],
    sortOption: SortOption = "recent"
  ) => {
    return useQuery({
      queryKey: [
        "test-responses",
        testId,
        page,
        pageSize,
        activeFilters,
        sortOption,
      ],
      queryFn: async () => {
        if (!testId) return { data: [], total: 0 };

        const { recruitmentFilters, integrityFilters, scoreFilters } = separateFilters(activeFilters);
        
        // If we have integrity or score filters, we need to fetch all data and filter client-side
        const needsClientSideFiltering = integrityFilters.length > 0 || scoreFilters.length > 0;
        
        let query = supabase
          .from("test_responses")
          .select(
            `
            id, status, started_at, completed_at, score, scores_confirmed, total_ai_score,
            recruitment_status, recruitment_notes, created_at, integrity_score,
            test_id, candidate_id, recruitment_updated_at, interview_invited_at, interview_invited_by,
            candidate:profiles(full_name, email, avatar_url, phone_number, cv_url, location),
            question_responses(
              id, question_id, response_content, score, 
              ai_feedback, evaluation_status, transcription_status, audio_url
            ),
            test:tests(id, title, questions!fk_test(id, marks, order, skill, difficulty, content, question_type, time_limit, markscheme)) 
          `,
            { count: needsClientSideFiltering ? undefined : "exact" }
          )
          .eq("test_id", testId)
          .in("status", ["completed", "in_progress"]);

        // Apply recruitment status filters (server-side)
        if (recruitmentFilters.length > 0) {
          query = query.in("recruitment_status", recruitmentFilters);
        }

        // Apply server-side sorting (only for database columns)
        switch (sortOption) {
          case "oldest":
            query = query.order("created_at", { ascending: true });
            break;
          case "recent":
          case "date":
            query = query.order("created_at", { ascending: false });
            break;
          case "highestIntegrity":
          case "integrity":
            query = query.order("integrity_score", { ascending: false, nullsFirst: false });
            break;
          case "candidate":
            query = query.order("candidate(full_name)", { ascending: true });
            break;
          default:
            query = query.order("created_at", { ascending: false });
            break;
        }

        // Apply pagination only if not doing client-side filtering
        if (!needsClientSideFiltering) {
          const from = (page - 1) * pageSize;
          const to = from + pageSize - 1;
          query = query.range(from, to);
        }

        const { data, error, count } = await query;

        if (error) {
          console.error(`Error fetching test responses for ${testId}:`, error);
          throw error;
        }
        
        // Transform data using utility function
        let transformedResponses = (data || []).map((response: any) => 
          transformTestResponse(response, testId, testDetails?.title)
        );

        // Apply client-side filtering if needed
        if (needsClientSideFiltering) {
          transformedResponses = applyClientSideFilters(transformedResponses, integrityFilters, scoreFilters);
          
          // Apply client-side pagination
          const from = (page - 1) * pageSize;
          const to = from + pageSize;
          const paginatedData = transformedResponses.slice(from, to);
          
          return {
            data: paginatedData,
            total: transformedResponses.length,
          };
        }

        return {
          data: transformedResponses,
          total: count || 0,
        };
      },
      enabled: !!testId && !!testDetails && !!questions,
      placeholderData: (previousData) => previousData,
    });
  };

  // Client-side query for fetching ALL responses (used for AI score sorting)
  const useAllResponsesQuery = (
    activeFilters: FilterType[] = []
  ) => {
    return useQuery({
      queryKey: [
        "all-test-responses",
        testId,
        activeFilters,
      ],
      queryFn: async () => {
        if (!testId) return [];

        const { recruitmentFilters, integrityFilters, scoreFilters } = separateFilters(activeFilters);

        let query = supabase
          .from("test_responses")
          .select(
            `
            id, status, started_at, completed_at, score, scores_confirmed, total_ai_score,
            recruitment_status, recruitment_notes, created_at, integrity_score,
            test_id, candidate_id, recruitment_updated_at, interview_invited_at, interview_invited_by,
            candidate:profiles(full_name, email, avatar_url, phone_number, cv_url, location),
            question_responses(
              id, question_id, response_content, score, 
              ai_feedback, evaluation_status, transcription_status, audio_url
            ),
            test:tests(id, title, questions!fk_test(id, marks, order, skill, difficulty, content, question_type, time_limit, markscheme)) 
          `
          )
          .eq("test_id", testId)
          .in("status", ["completed", "in_progress"]);

        // Apply recruitment status filters (server-side)
        if (recruitmentFilters.length > 0) {
          query = query.in("recruitment_status", recruitmentFilters);
        }

        // Order by created_at for consistent results
        query = query.order("created_at", { ascending: false });

        const { data, error } = await query;

        if (error) {
          console.error(`Error fetching all test responses for ${testId}:`, error);
          throw error;
        }
        
        // Transform data using utility function
        let transformedResponses = (data || []).map((response: any) => 
          transformTestResponse(response, testId, testDetails?.title)
        );

        // Apply client-side filters
        transformedResponses = applyClientSideFilters(transformedResponses, integrityFilters, scoreFilters);

        return transformedResponses;
      },
      enabled: !!testId && !!testDetails && !!questions,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  return {
    testDetails,
    questions,
    useResponsesQuery,
    useAllResponsesQuery,
    isLoadingTest,
    isLoadingQuestions,
    testError,
    questionsError,
    isError: !!testError || !!questionsError,
    isLoading: isLoadingTest || isLoadingQuestions,
  };
};
